import React, { useState } from 'react';
import { CrossCircledIcon } from '@radix-ui/react-icons';
import { apiUtils } from '../../../utils/apiUtils';

/**
 * SmartRetry Component - Intelligently determines and executes the best retry strategy
 * 
 * Determines whether a scan failed during scraping or analysis phase and provides
 * appropriate retry options with clear explanations.
 */
export const SmartRetry = ({
	scanId,
	onRetryStarted,
	onRetryComplete,
	onError,
	className = "",
	showDetails = true
}) => {
	const [isAnalyzing, setIsAnalyzing] = useState(false);
	const [isRetrying, setIsRetrying] = useState(false);
	const [retryStrategy, setRetryStrategy] = useState(null);
	const [error, setError] = useState(null);

	/**
	 * Analyze the scan failure and determine the best retry strategy
	 */
	const analyzeFailure = async () => {
		if (!scanId) {
			console.error('SmartRetry: No scanId provided');
			setError('No scan ID provided');
			return;
		}

		setIsAnalyzing(true);
		setError(null);

		try {
			const response = await apiUtils.get(`/api/scans/${scanId}/retry-strategy`);

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: Failed to analyze scan failure`);
			}

			const result = await response.json();
			console.log('Retry strategy response:', result);

			// Validate the response structure
			if (!result.data) {
				throw new Error('Invalid response: missing data field');
			}

			setRetryStrategy(result.data);

		} catch (err) {
			console.error('Error analyzing scan failure:', err);
			console.error('Error details:', err.stack);
			setError(err.message);
			if (onError) onError(err);
		} finally {
			setIsAnalyzing(false);
		}
	};

	/**
	 * Execute the retry with the determined strategy
	 */
	const executeRetry = async (force = false) => {
		if (!scanId) return;

		setIsRetrying(true);
		setError(null);

		try {
			if (onRetryStarted) {
				onRetryStarted(retryStrategy?.phase || 'unknown');
			}

			const response = await apiUtils.post(`/api/scans/${scanId}/retry`, {
				force
			});

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: Failed to retry scan`);
			}

			const result = await response.json();

			if (onRetryComplete) {
				onRetryComplete(result.data);
			}

			// Update the retry strategy with the results
			setRetryStrategy(prev => ({
				...prev,
				executed: true,
				result: result.data
			}));

		} catch (err) {
			console.error('Error executing retry:', err);
			setError(err.message);
			if (onError) onError(err);
		} finally {
			setIsRetrying(false);
		}
	};

	/**
	 * Get display information for the retry strategy
	 */
	const getRetryInfo = (strategy) => {
		switch (strategy?.phase) {
			case 'scraping':
				return {
					title: 'Restart Scraping',
					description: 'Failed during data collection. Will restart from the beginning.',
					action: 'Re-scrape from start',
					color: 'bg-brand-50 border-brand-200 text-brand-800 dark:bg-brand-500/10 dark:border-brand-500/30 dark:text-brand-200',
					buttonColor: 'bg-brand-600 hover:bg-brand-700 text-white',
					impact: 'All data will be cleared and re-collected'
				};
			case 'analysis':
				return {
					title: 'Retry Analysis',
					description: 'Data collection succeeded but analysis failed. Will re-analyze existing data.',
					action: 'Re-analyze existing posts',
					color: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-500/10 dark:border-green-500/30 dark:text-green-200',
					buttonColor: 'bg-green-600 hover:bg-green-700 text-white',
					impact: 'Existing posts will be re-analyzed'
				};
			case 'partial':
				return {
					title: 'Targeted Retry',
					description: 'Some windows/jobs failed while others succeeded. Will retry only the failed components.',
					action: 'Retry failed parts only',
					color: 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-500/10 dark:border-yellow-500/30 dark:text-yellow-200',
					buttonColor: 'bg-yellow-600 hover:bg-yellow-700 text-white',
					impact: 'Only failed windows/jobs will be retried - successful data remains intact'
				};
			default:
				return {
					title: 'Unknown Strategy',
					description: 'Unable to determine the best retry approach.',
					action: 'Manual retry needed',
					color: 'bg-muted border-border text-foreground',
					buttonColor: 'bg-neutral-600 hover:bg-neutral-700 text-white',
					impact: 'Contact support for assistance'
				};
		}
	};

	// Auto-analyze on first render if we have a scanId
	React.useEffect(() => {
		if (scanId && !retryStrategy && !isAnalyzing) {
			analyzeFailure();
		}
		// Auto-analyse once per scanId. analyzeFailure/retryStrategy/isAnalyzing
		// change as a result of running it; depending on them would re-trigger.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scanId]);

	if (error) {
		return (
			<div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${className} dark:bg-red-500/10 dark:border-red-500/30`}>
				<div className="flex items-center space-x-2 text-red-800">
					<CrossCircledIcon className="h-5 w-5 text-red-600" />
					<span className="font-medium">Error analyzing scan failure</span>
				</div>
				<p className="mt-2 text-sm text-red-700">{error}</p>
				<button
					onClick={analyzeFailure}
					className="mt-3 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
					disabled={isAnalyzing}
				>
					Try Again
				</button>
			</div>
		);
	}

	if (isAnalyzing) {
		return (
			<div className={`p-4 bg-brand-50 border border-brand-200 rounded-lg ${className} dark:bg-brand-500/10 dark:border-brand-500/30`}>
				<div className="flex items-center space-x-2 text-brand-800">
					<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600"></div>
					<span className="font-medium">Analyzing scan failure...</span>
				</div>
				<p className="mt-2 text-sm text-brand-700">
					Determining the best retry strategy based on failure patterns...
				</p>
			</div>
		);
	}

	if (!retryStrategy) {
		return (
			<div className={`p-4 bg-muted border border-border rounded-lg ${className}`}>
				<button
					onClick={analyzeFailure}
					className="px-4 py-2 bg-brand-600 text-white rounded hover:bg-brand-700 transition-colors"
					disabled={isAnalyzing}
				>
					Analyze Scan Failure
				</button>
			</div>
		);
	}

	const retryInfo = getRetryInfo(retryStrategy);

	return (
		<div className={`p-4 border rounded-lg ${retryInfo.color} ${className}`}>
			<div className="space-y-3">
				{/* Header */}
				<div>
					<h3 className="font-medium text-lg">{retryInfo.title}</h3>
					<p className="text-sm mt-1">{retryInfo.description}</p>
				</div>

				{/* Failure Reason */}
				<div className="text-sm">
					<span className="font-medium">Reason: </span>
					<span>{retryStrategy.reason}</span>
				</div>

				{/* Detailed Stats (if enabled) */}
				{showDetails && retryStrategy.stats && (
					<details className="text-sm">
						<summary className="cursor-pointer font-medium mb-2 hover:underline">
							View Detailed Analysis
						</summary>
						<div className="pl-4 space-y-1 text-xs bg-card/50 rounded p-2">
							<div>Total Posts: {retryStrategy.stats.totalPosts}</div>
							<div>Analyzed Posts: {retryStrategy.stats.analyzedPosts}</div>
							<div>Pending Analysis: {retryStrategy.stats.pendingAnalysisPosts}</div>
							<div>Failed Jobs: {retryStrategy.stats.failedJobs}</div>
							<div>Completed Jobs: {retryStrategy.stats.completedJobs}</div>
							<div>Failed Scraping Windows: {retryStrategy.stats.failedScrapingWindows}</div>
							<div>Failed Analysis Windows: {retryStrategy.stats.failedAnalysisWindows}</div>
						</div>
					</details>
				)}

				{/* Impact Warning */}
				<div className="text-sm bg-card/50 rounded p-2">
					<span className="font-medium">Impact: </span>
					<span>{retryInfo.impact}</span>
				</div>

				{/* Action Buttons */}
				<div className="flex space-x-2 pt-2">
					<button
						onClick={() => executeRetry(false)}
						disabled={isRetrying}
						className={`px-4 py-2 rounded transition-colors ${retryInfo.buttonColor} ${isRetrying ? 'opacity-50 cursor-not-allowed' : ''
							}`}
					>
						{isRetrying ? (
							<span className="flex items-center space-x-2">
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
								<span>Retrying...</span>
							</span>
						) : (
							retryInfo.action
						)}
					</button>

					{/* Force retry option for completed scans */}
					{retryStrategy.phase === 'scraping' && (
						<button
							onClick={() => executeRetry(true)}
							disabled={isRetrying}
							className="px-4 py-2 bg-neutral-600 text-white rounded hover:bg-neutral-700 transition-colors"
						>
							Force Retry
						</button>
					)}
				</div>

				{/* Success Message */}
				{retryStrategy.executed && retryStrategy.result && (
					<div className="mt-3 p-2 bg-green-100 border border-green-300 rounded text-green-800 text-sm dark:bg-green-500/15 dark:border-green-500/30 dark:text-green-300">
						Retry initiated. {retryStrategy.result.message}
					</div>
				)}
			</div>
		</div>
	);
};

export default SmartRetry;
